import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PUBLIC_ORIGIN } from "../config";
import { routes } from "../entry-server";

/**
 * Updates <title>, <meta name="description">, and <link rel="canonical"> dynamically
 * on every client-side route change, guaranteeing that:
 * 1. The canonical is ALWAYS self-canonical (matches the current route exactly).
 * 2. Titles and descriptions match the active page in the browser DOM and SEO extensions.
 */
export function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname.replace(/\/$/, "") || "/";
    const origin = PUBLIC_ORIGIN || window.location.origin;

    // Find route metadata matching current path
    const meta = routes.find((r) => r.path === pathname) || {
      title: "LimeShare — Zero-Knowledge Encrypted File Transfer",
      description: "Send large files up to 3 GB with end-to-end client encryption. No accounts, auto-purged.",
    };

    // 1. Update Title
    document.title = meta.title;

    // 2. Update Self-Canonical (MUST ALWAYS MATCH CURRENT PATH)
    const selfCanonicalUrl = `${origin}${pathname === "/" ? "/" : pathname}`;
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", selfCanonicalUrl);

    // 3. Update Meta Description
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
    }
    descTag.setAttribute("content", meta.description);

    // 4. Update OpenGraph Tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", meta.title);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute("content", meta.description);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", selfCanonicalUrl);
  }, [location.pathname]);

  return null;
}
